import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { useAuth } from '../context/AuthContext';
import {
  fetchDocumentBytes,
  getDocumentCoords,
  listAnnotations,
  createAnnotation,
  deleteAnnotation,
  addReply,
  deleteReply,
  getDocument,
  createShare,
  listShares,
  revokeShare,
} from '../services/api';
import toast from 'react-hot-toast';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const COLORS = [
  { name: 'amber', bg: '#fde68a', border: '#f59e0b' },
  { name: 'blue', bg: '#bfdbfe', border: '#3b82f6' },
  { name: 'green', bg: '#bbf7d0', border: '#22c55e' },
  { name: 'pink', bg: '#fbcfe8', border: '#ec4899' },
];

const ZOOM = 1.5;

type Token = { x: number; y: number; width: number; height: number; text: string };
type PageCoords = { page: { width: number; height: number; index: number }; tokens: Token[] };
type Reply = {
  reply_id: string;
  text: string;
  created_at: string;
  created_by: string;
  author_name?: string;
};
type Annotation = {
  anno_id: string;
  page_index: number;
  boxes: { x: number; y: number; width: number; height: number }[];
  text: string;
  comment: string;
  color: string;
  created_at: string;
  created_by?: string;
  author_name?: string;
  replies?: Reply[];
};
type PendingSelection = {
  pageIndex: number;
  tokens: Token[];
  bounds: { x: number; y: number; width: number; height: number };
};

export default function AnnotatorPage() {
  const { docId } = useParams<{ docId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [docMeta, setDocMeta] = useState<any | null>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [pages, setPages] = useState<PageCoords[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [coordsLoading, setCoordsLoading] = useState(false);
  const [color, setColor] = useState(COLORS[0]);
  const [pendingSelection, setPendingSelection] = useState<PendingSelection | null>(null);
  const [comment, setComment] = useState('');
  const [visiblePage, setVisiblePage] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareToken, setShareToken] = useState<string | null>(null);

  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const scrollerRef = useRef<HTMLDivElement>(null);

  // ─── Load document, PDF, annotations ─────────────────────────────────
  useEffect(() => {
    if (!currentUser || !docId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await currentUser.getIdToken();
        const [meta, bytes, annoRes] = await Promise.all([
          getDocument(token, docId),
          fetchDocumentBytes(token, docId),
          listAnnotations(token, docId),
        ]);
        if (cancelled) return;
        setDocMeta(meta.document);
        setAnnotations(annoRes.annotations || []);
        const pdfDoc = await pdfjs.getDocument({ data: bytes }).promise;
        if (cancelled) return;
        setPdf(pdfDoc);
      } catch (e: any) {
        toast.error(`Failed to load: ${e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, docId]);

  // Poll until OCR is ready. Backend returns one of:
  //   { status: 'ready', pages }  — done
  //   { status: 'pending' }       — keep polling
  //   { status: 'failed', error } — show error, stop
  const ensureCoords = useCallback(async () => {
    if (!currentUser || !docId || pages.length > 0 || coordsLoading) return;
    setCoordsLoading(true);
    let attempts = 0;
    const maxAttempts = 80;  // 80 * 3s = 4 min cap
    try {
      const token = await currentUser.getIdToken();
      while (attempts < maxAttempts) {
        const res = await getDocumentCoords(token, docId);
        if (res.status === 'ready') {
          setPages(res.pages || []);
          if (attempts > 0) toast.success('Tokens extracted');
          return;
        }
        if (res.status === 'failed') {
          toast.error(`OCR failed: ${res.error || 'unknown'}`);
          return;
        }
        await new Promise((r) => setTimeout(r, 3000));
        attempts++;
      }
      toast.error('OCR is taking unusually long — try again later');
    } catch (e: any) {
      toast.error(`Coords failed: ${e.message}`);
    } finally {
      setCoordsLoading(false);
    }
  }, [currentUser, docId, pages.length, coordsLoading]);

  // ─── Track visible page via IntersectionObserver ─────────────────────
  useEffect(() => {
    if (!pdf || !scrollerRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const idx = Number((visible.target as HTMLElement).dataset.pageIndex);
          if (!Number.isNaN(idx)) setVisiblePage(idx);
        }
      },
      { root: scrollerRef.current, threshold: [0.25, 0.5, 0.75] }
    );
    Object.values(pageRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [pdf]);

  const scrollToPage = (idx: number) => {
    pageRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─── Keyboard: Esc to cancel pending selection ───────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pendingSelection) {
        setPendingSelection(null);
        setComment('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingSelection]);

  const onPageSelect = (pageIndex: number, tokens: Token[]) => {
    if (tokens.length === 0) { setPendingSelection(null); return; }
    const minX = Math.min(...tokens.map((t) => t.x));
    const minY = Math.min(...tokens.map((t) => t.y));
    const maxX = Math.max(...tokens.map((t) => t.x + t.width));
    const maxY = Math.max(...tokens.map((t) => t.y + t.height));
    setPendingSelection({
      pageIndex,
      tokens,
      bounds: { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
    });
    setComment('');
  };

  const saveAnnotation = async () => {
    if (!currentUser || !docId || !pendingSelection) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await createAnnotation(token, docId, {
        page_index: pendingSelection.pageIndex,
        boxes: [pendingSelection.bounds],
        text: pendingSelection.tokens.map((t) => t.text).join(' '),
        comment,
        color: color.bg,
      });
      setAnnotations((prev) => [...prev, res]);
      setPendingSelection(null);
      setComment('');
      toast.success('Annotation saved');
    } catch (e: any) {
      toast.error(e.message || 'Save failed');
    }
  };

  const handleDelete = async (annoId: string) => {
    if (!currentUser || !docId) return;
    if (!window.confirm('Delete this annotation?')) return;
    try {
      const token = await currentUser.getIdToken();
      await deleteAnnotation(token, docId, annoId);
      setAnnotations((prev) => prev.filter((a) => a.anno_id !== annoId));
    } catch (e: any) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const handleAddReply = async (annoId: string, text: string) => {
    if (!currentUser || !docId || !text.trim()) return;
    try {
      const token = await currentUser.getIdToken();
      const reply = await addReply(token, docId, annoId, text.trim());
      setAnnotations((prev) => prev.map((a) =>
        a.anno_id === annoId ? { ...a, replies: [...(a.replies || []), reply] } : a
      ));
    } catch (e: any) {
      toast.error(e.message || 'Reply failed');
    }
  };

  const openShare = async () => {
    if (!currentUser || !docId) return;
    setShareOpen(true);
    try {
      const token = await currentUser.getIdToken();
      const existing = await listShares(token, docId);
      const active = (existing.shares || [])[0];
      const share = active || (await createShare(token, docId));
      setShareToken(share.share_token);
      setShareUrl(`${window.location.origin}/share/${share.share_token}`);
    } catch (e: any) {
      toast.error(e.message || 'Could not create share link');
      setShareOpen(false);
    }
  };

  const copyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed — select the URL manually');
    }
  };

  const revokeCurrentShare = async () => {
    if (!currentUser || !shareToken) return;
    if (!window.confirm('Revoke this share link? Anyone with the URL will lose access.')) return;
    try {
      const token = await currentUser.getIdToken();
      await revokeShare(token, shareToken);
      setShareToken(null);
      setShareUrl(null);
      setShareOpen(false);
      toast.success('Link revoked');
    } catch (e: any) {
      toast.error(e.message || 'Revoke failed');
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({
      document: docMeta ? {
        doc_id: docMeta.doc_id,
        label: docMeta.label,
        filename: docMeta.filename,
      } : null,
      annotations,
    }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(docMeta?.label || 'document').replace(/[^\w一-龥\-]+/g, '_')}-annotations.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteReply = async (annoId: string, replyId: string) => {
    if (!currentUser || !docId) return;
    try {
      const token = await currentUser.getIdToken();
      await deleteReply(token, docId, annoId, replyId);
      setAnnotations((prev) => prev.map((a) =>
        a.anno_id === annoId
          ? { ...a, replies: (a.replies || []).filter((r) => r.reply_id !== replyId) }
          : a
      ));
    } catch (e: any) {
      toast.error(e.message || 'Delete failed');
    }
  };

  if (loading) return <div className="p-12 text-center text-gray-400">Loading document…</div>;
  if (!pdf) return <div className="p-12 text-center text-gray-400">Could not open this PDF.</div>;

  return (
    <div className="flex gap-4 max-w-[1400px]">
      <div className="flex-1 min-w-0 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <button onClick={() => navigate('/app/documents')}
              className="text-xs text-gray-500 hover:text-blue-600 mb-1">← Documents</button>
            <h1 className="text-lg font-semibold text-gray-800 truncate">{docMeta?.label || 'Document'}</h1>
          </div>
          <div className="flex items-center gap-2">
            {COLORS.map((c) => (
              <button key={c.name} onClick={() => setColor(c)}
                className={`w-7 h-7 rounded-full border-2 ${color.name === c.name ? 'border-slate-700' : 'border-transparent'}`}
                style={{ backgroundColor: c.bg }} title={c.name} />
            ))}
            <button onClick={openShare}
              className="ml-2 px-3 py-1.5 border border-gray-300 text-gray-700 rounded text-xs hover:bg-gray-50">
              Share
            </button>
            <button onClick={exportJson} disabled={annotations.length === 0}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded text-xs hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
              Export JSON
            </button>
          </div>
        </div>

        {shareOpen && shareUrl && (
          <div className="bg-white border border-blue-200 rounded-xl p-3 flex items-center gap-2">
            <span className="text-xs text-gray-500 flex-shrink-0">Read-only link:</span>
            <input readOnly value={shareUrl} onClick={(e) => e.currentTarget.select()}
              className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs bg-gray-50 font-mono" />
            <button onClick={copyShareUrl}
              className="px-3 py-1 bg-blue-700 text-white rounded text-xs hover:bg-blue-600">Copy</button>
            <button onClick={revokeCurrentShare}
              className="px-3 py-1 border border-gray-300 text-red-600 rounded text-xs hover:bg-red-50">Revoke</button>
            <button onClick={() => setShareOpen(false)}
              className="text-gray-400 hover:text-gray-600 px-1">×</button>
          </div>
        )}

        {/* Page nav (jump-to) + OCR enable */}
        <div className="flex items-center gap-2 text-sm sticky top-0 z-10 bg-gray-50 py-2">
          <button disabled={visiblePage === 0} onClick={() => scrollToPage(visiblePage - 1)}
            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-30 bg-white">←</button>
          <span className="text-gray-600">Page {visiblePage + 1} / {pdf.numPages}</span>
          <button disabled={visiblePage >= pdf.numPages - 1} onClick={() => scrollToPage(visiblePage + 1)}
            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-30 bg-white">→</button>
          {pages.length === 0 && (
            <button onClick={ensureCoords} disabled={coordsLoading}
              className="ml-auto px-3 py-1 bg-blue-700 text-white rounded text-xs hover:bg-blue-600 disabled:opacity-50">
              {coordsLoading ? 'Extracting tokens (OCR)…' : 'Enable annotation (OCR)'}
            </button>
          )}
        </div>

        {/* Page stack */}
        <div ref={scrollerRef}
          className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
          {Array.from({ length: pdf.numPages }, (_, i) => (
            <PdfPageView
              key={i}
              ref={(el: HTMLDivElement | null) => { pageRefs.current[i] = el; }}
              pdf={pdf}
              pageIndex={i}
              coords={pages[i]}
              annotations={annotations.filter((a) => a.page_index === i)}
              onSelect={(tokens) => onPageSelect(i, tokens)}
              color={color}
              pendingSelection={pendingSelection?.pageIndex === i ? pendingSelection : null}
            />
          ))}
        </div>

        {pages.length === 0 && !coordsLoading && (
          <p className="text-xs text-gray-400">
            Token coords not yet extracted. Click "Enable annotation (OCR)" to OCR this PDF and unlock click-drag highlighting. Cached after first run.
          </p>
        )}
      </div>

      {/* Annotation sidebar */}
      <aside className="w-80 flex-shrink-0 space-y-3">
        {pendingSelection && (
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">
              New annotation · p.{pendingSelection.pageIndex + 1}
            </div>
            <div className="text-sm text-gray-700 italic line-clamp-3">
              "{pendingSelection.tokens.map((t) => t.text).join(' ')}"
            </div>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  saveAnnotation();
                }
              }}
              autoFocus
              placeholder="Add a comment (optional)…"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <div className="flex gap-2">
              <button onClick={saveAnnotation}
                className="flex-1 px-3 py-1.5 bg-blue-700 text-white text-xs rounded hover:bg-blue-600">Save</button>
              <button onClick={() => { setPendingSelection(null); setComment(''); }}
                className="px-3 py-1.5 border border-gray-300 text-gray-500 text-xs rounded hover:bg-gray-50">Cancel</button>
            </div>
            <p className="text-[10px] text-gray-400">⌘/Ctrl+Enter to save · Esc to cancel</p>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            Annotations ({annotations.length})
          </div>
          {annotations.length === 0 ? (
            <p className="text-xs text-gray-400">None yet. Drag across the page to highlight text.</p>
          ) : (
            <ul className="space-y-4">
              {annotations.map((a) => (
                <AnnotationItem key={a.anno_id} annotation={a}
                  currentUid={currentUser?.uid}
                  onJump={() => scrollToPage(a.page_index)}
                  onDelete={() => handleDelete(a.anno_id)}
                  onAddReply={(text) => handleAddReply(a.anno_id, text)}
                  onDeleteReply={(rid) => handleDeleteReply(a.anno_id, rid)} />
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

// ─── Per-page view: renders the canvas + overlay + drag-select ─────────

interface PdfPageViewProps {
  pdf: PDFDocumentProxy;
  pageIndex: number;
  coords: PageCoords | undefined;
  annotations: Annotation[];
  onSelect: (tokens: Token[]) => void;
  color: { bg: string; border: string };
  pendingSelection: PendingSelection | null;
}

const PdfPageView = React.forwardRef<HTMLDivElement, PdfPageViewProps>(
  ({ pdf, pageIndex, coords, annotations, onSelect, color, pendingSelection }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<{ startX: number; startY: number } | null>(null);
    const [dragRect, setDragRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
    const [size, setSize] = useState<{ w: number; h: number } | null>(null);

    useEffect(() => {
      let cancelled = false;
      (async () => {
        const page: PDFPageProxy = await pdf.getPage(pageIndex + 1);
        if (cancelled || !canvasRef.current) return;
        const viewport = page.getViewport({ scale: ZOOM });
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d')!;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (!cancelled) setSize({ w: viewport.width, h: viewport.height });
      })();
      return () => { cancelled = true; };
    }, [pdf, pageIndex]);

    const onMouseDown = (e: React.MouseEvent) => {
      if (!overlayRef.current || !coords) return;
      const rect = overlayRef.current.getBoundingClientRect();
      dragRef.current = { startX: e.clientX - rect.left, startY: e.clientY - rect.top };
      setDragRect({ x: dragRef.current.startX, y: dragRef.current.startY, w: 0, h: 0 });
    };
    const onMouseMove = (e: React.MouseEvent) => {
      if (!dragRef.current || !overlayRef.current) return;
      const rect = overlayRef.current.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const { startX, startY } = dragRef.current;
      setDragRect({
        x: Math.min(startX, cx),
        y: Math.min(startY, cy),
        w: Math.abs(cx - startX),
        h: Math.abs(cy - startY),
      });
    };
    const onMouseUp = () => {
      if (!dragRef.current || !dragRect || !coords) {
        dragRef.current = null;
        setDragRect(null);
        return;
      }
      const sel = { x: dragRect.x / ZOOM, y: dragRect.y / ZOOM, w: dragRect.w / ZOOM, h: dragRect.h / ZOOM };

      // Distinguish click (no meaningful drag) from drag-select.
      // <4px movement → treat as click → select the whole line.
      const CLICK_THRESHOLD_PX = 4;
      const isClick = dragRect.w < CLICK_THRESHOLD_PX && dragRect.h < CLICK_THRESHOLD_PX;
      let hits;
      if (isClick) {
        const target = coords.tokens.find((t) =>
          sel.x >= t.x && sel.x <= t.x + t.width &&
          sel.y >= t.y && sel.y <= t.y + t.height
        );
        hits = target ? selectLine(coords.tokens, target) : [];
      } else {
        hits = coords.tokens.filter((t) =>
          t.x + t.width >= sel.x && t.x <= sel.x + sel.w &&
          t.y + t.height >= sel.y && t.y <= sel.y + sel.h
        );
      }
      dragRef.current = null;
      setDragRect(null);
      onSelect(hits);
    };

    return (
      <div ref={ref} data-page-index={pageIndex} className="relative inline-block bg-white shadow border border-gray-200">
        <div className="absolute -left-10 top-2 text-xs text-gray-400 select-none">{pageIndex + 1}</div>
        <canvas ref={canvasRef} className="block" />
        {size && (
          <div ref={overlayRef}
            onMouseDown={coords ? onMouseDown : undefined}
            onMouseMove={coords ? onMouseMove : undefined}
            onMouseUp={coords ? onMouseUp : undefined}
            className="absolute inset-0"
            style={{ cursor: coords ? 'crosshair' : 'default', width: size.w, height: size.h }}>
            {/* Saved annotations */}
            {annotations.map((a) => a.boxes.map((b, i) => (
              <div key={`${a.anno_id}-${i}`} className="absolute pointer-events-none"
                style={{
                  left: b.x * ZOOM, top: b.y * ZOOM,
                  width: b.width * ZOOM, height: b.height * ZOOM,
                  backgroundColor: a.color, opacity: 0.4,
                  border: `1px solid ${a.color}`,
                }}
                title={a.comment} />
            )))}
            {/* In-flight drag rectangle */}
            {dragRect && (
              <div className="absolute pointer-events-none border-2 border-dashed"
                style={{
                  left: dragRect.x, top: dragRect.y,
                  width: dragRect.w, height: dragRect.h,
                  borderColor: color.border, backgroundColor: color.bg, opacity: 0.3,
                }} />
            )}
            {/* Pending selection (awaiting comment) */}
            {pendingSelection && (
              <div className="absolute pointer-events-none border-2"
                style={{
                  left: pendingSelection.bounds.x * ZOOM, top: pendingSelection.bounds.y * ZOOM,
                  width: pendingSelection.bounds.width * ZOOM, height: pendingSelection.bounds.height * ZOOM,
                  borderColor: color.border, backgroundColor: color.bg, opacity: 0.5,
                }} />
            )}
          </div>
        )}
      </div>
    );
  }
);
PdfPageView.displayName = 'PdfPageView';

// ─── Annotation list item with replies ─────────────────────────────────

interface AnnotationItemProps {
  annotation: Annotation;
  currentUid?: string;
  onJump: () => void;
  onDelete: () => void;
  onAddReply: (text: string) => void;
  onDeleteReply: (replyId: string) => void;
}

function AnnotationItem({ annotation, currentUid, onJump, onDelete, onAddReply, onDeleteReply }: AnnotationItemProps) {
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const replies = annotation.replies || [];

  const submit = () => {
    if (!replyText.trim()) return;
    onAddReply(replyText);
    setReplyText('');
    setReplying(false);
  };

  return (
    <li className="text-xs border-l-2 pl-3 py-1" style={{ borderColor: annotation.color }}>
      <div className="flex items-start justify-between gap-2 cursor-pointer" onClick={onJump}>
        <div className="min-w-0">
          <div className="text-gray-400">
            p.{annotation.page_index + 1}
            {annotation.author_name && <span className="ml-1">· {annotation.author_name}</span>}
          </div>
          <div className="italic text-gray-700 line-clamp-2">"{annotation.text}"</div>
          {annotation.comment && <div className="text-gray-600 mt-1">{annotation.comment}</div>}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-gray-300 hover:text-red-500">×</button>
      </div>

      {replies.length > 0 && (
        <ul className="mt-2 ml-2 space-y-1.5 border-l border-gray-200 pl-3">
          {replies.map((r) => (
            <li key={r.reply_id} className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] text-gray-400">{r.author_name || 'Anonymous'}</div>
                <div className="text-gray-700">{r.text}</div>
              </div>
              {r.created_by === currentUid && (
                <button onClick={() => onDeleteReply(r.reply_id)}
                  className="text-gray-300 hover:text-red-500">×</button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 ml-2">
        {replying ? (
          <div className="space-y-1">
            <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); }
                if (e.key === 'Escape') { setReplying(false); setReplyText(''); }
              }}
              autoFocus rows={2}
              placeholder="Reply…"
              className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
            <div className="flex gap-1">
              <button onClick={submit}
                className="px-2 py-0.5 bg-blue-700 text-white rounded text-[10px] hover:bg-blue-600">Reply</button>
              <button onClick={() => { setReplying(false); setReplyText(''); }}
                className="px-2 py-0.5 text-gray-500 text-[10px] hover:text-gray-700">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setReplying(true)}
            className="text-[10px] text-gray-400 hover:text-blue-600">+ reply</button>
        )}
      </div>
    </li>
  );
}

// Group tokens that share a horizontal line with `target`, by overlap of
// vertical extent — works without backend `line_num` because tokens on
// the same line have ~the same y range. Sort left-to-right so the
// resulting text reads naturally.
function selectLine(tokens: Token[], target: Token): Token[] {
  const targetMid = target.y + target.height / 2;
  const tolerance = target.height * 0.6;
  return tokens
    .filter((t) => Math.abs((t.y + t.height / 2) - targetMid) <= tolerance)
    .sort((a, b) => a.x - b.x);
}
